/**
 * LensPresetSelector — Table toolbar lens switching
 *
 * Dropdown menu that lets users switch between lens presets for table column
 * filtering. Each lens narrows which columns are visible based on the user's
 * role, scope, and context (DRR-075 4-phase lens system).
 *
 * Appears in the TabulatorPrimitiveManager toolbar next to the column toggle.
 * Only rendered when activeLenses are available.
 *
 * FERP Principles:
 * - Dumb Frontend: lens definitions from backend, not hardcoded
 * - Data-driven: available presets from useLensEvaluation().activeLenses
 */

import {
  Menu,
  ActionIcon,
  Tooltip,
  Text,
  Badge,
  Group,
  Loader,
} from '@mantine/core';
import { IconFilter, IconFilterOff, IconCheck } from '@tabler/icons-react';
import type { ActiveLens } from '@ferp/workflow-forms';

// ============================================================================
// TYPES
// ============================================================================

interface LensPresetSelectorProps {
  /** Available lenses from useLensEvaluation().activeLenses */
  activeLenses: ActiveLens[];
  /** Currently selected lens ID, or null for "All Fields" */
  selectedLensId: string | null;
  /** Callback when user selects a lens (null = clear filter) */
  onSelect: (lensId: string | null) => void;
  /** Whether lens evaluation is in progress */
  loading?: boolean;
}

// ============================================================================
// PHASE COLORS
// ============================================================================

const PHASE_COLORS: Record<string, string> = {
  role: 'blue',
  scope: 'teal',
  setting: 'orange',
  situation: 'red',
};

// ============================================================================
// COMPONENT
// ============================================================================

export function LensPresetSelector({
  activeLenses,
  selectedLensId,
  onSelect,
  loading = false,
}: LensPresetSelectorProps) {
  const hasSelection = selectedLensId !== null;

  return (
    <Menu shadow="md" width={300} closeOnItemClick>
      <Menu.Target>
        <Tooltip label={hasSelection ? 'Lens filter active' : 'Filter columns by lens'}>
          <ActionIcon
            variant={hasSelection ? 'filled' : 'light'}
            size="lg"
            color={hasSelection ? 'blue' : undefined}
            loading={loading}
          >
            <IconFilter size={18} />
          </ActionIcon>
        </Tooltip>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label>Column Lens Presets</Menu.Label>

        {/* All Fields (clear lens) */}
        <Menu.Item
          onClick={() => onSelect(null)}
          leftSection={
            !hasSelection ? (
              <IconCheck size={14} color="var(--mantine-color-green-6)" />
            ) : (
              <IconFilterOff size={14} />
            )
          }
        >
          <Text size="sm" fw={!hasSelection ? 600 : undefined}>
            All Fields
          </Text>
        </Menu.Item>

        <Menu.Divider />

        {/* Loading state */}
        {loading && activeLenses.length === 0 && (
          <Menu.Item disabled>
            <Group gap="xs">
              <Loader size="xs" />
              <Text size="sm" c="dimmed">Loading lenses...</Text>
            </Group>
          </Menu.Item>
        )}

        {/* No lenses available */}
        {!loading && activeLenses.length === 0 && (
          <Menu.Item disabled>
            <Text size="sm" c="dimmed">No lenses configured</Text>
          </Menu.Item>
        )}

        {/* Lens options */}
        {activeLenses.map((lens) => {
          const isSelected = selectedLensId === lens.lensId;
          return (
            <Menu.Item
              key={lens.lensId}
              onClick={() => onSelect(lens.lensId)}
              leftSection={
                isSelected ? (
                  <IconCheck size={14} color="var(--mantine-color-green-6)" />
                ) : (
                  <span style={{ width: 14 }} />
                )
              }
            >
              <Group gap="xs" justify="space-between" wrap="nowrap">
                <Text size="sm" fw={isSelected ? 600 : undefined} lineClamp={1}>
                  {lens.name}
                </Text>
                <Badge
                  size="xs"
                  variant="light"
                  color={PHASE_COLORS[lens.phase] ?? 'gray'}
                >
                  {lens.phase}
                </Badge>
              </Group>
            </Menu.Item>
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );
}
